/**
 * Story #3409 — On-demand patent translation panel.
 *
 * Renders a language picker + Translate button inside the patent detail
 * view. On click, calls translatePatent(id, lang) → backend caches in
 * OpenSearch, so the second click for the same lang is a cache hit.
 *
 * Only title/abstract/claims are translated. Patent IDs, CPC codes,
 * inventor names, jurisdiction codes, and dates are identifiers and
 * never translated.
 *
 * Round 2 fix (post-CoWork Round 1): the original native <select> rendered
 * only the focused option visibly in some browser/OS combinations (Round 1
 * screenshot showed dropdown popup with only "English" visible despite all
 * 12 options being in the bundle and the .map() compiling correctly). The
 * picker is now a custom listbox built from <button> elements with explicit
 * text-slate-900 colour, so option text is always visible regardless of
 * browser native dropdown quirks or user-agent stylesheets.
 *
 * Marker: PATENT-TRANSLATE-AA.
 */

import { useEffect, useRef, useState } from 'react'
import { Languages, ChevronDown, Check } from 'lucide-react'
import { translatePatent, type PatentTranslationResult } from '../../services/api'

interface Props {
  patentId: string
  sourceLang?: string | null
  sourceTitle?: string
  sourceAbstract?: string
  sourceClaims?: string[]
}

// Twelve target languages with native labels — must stay in lockstep with
// SUPPORTED_TARGETS in lambda/innovateflow-api/patents/translate.py.
const LANGS: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'zh-Hans', label: '简体中文 (Simplified Chinese)' },
  { code: 'zh-Hant', label: '繁體中文 (Traditional Chinese)' },
  { code: 'ja', label: '日本語 (Japanese)' },
  { code: 'ko', label: '한국어 (Korean)' },
  { code: 'de', label: 'Deutsch (German)' },
  { code: 'fr', label: 'Français (French)' },
  { code: 'es', label: 'Español (Spanish)' },
  { code: 'pt', label: 'Português (Portuguese)' },
  { code: 'it', label: 'Italiano (Italian)' },
  { code: 'ru', label: 'Русский (Russian)' },
  { code: 'ar', label: 'العربية (Arabic)' },
]

// CoWork Round 2 fallback: the OpenSearch patent records have `jurisdiction`
// but typically don't carry an explicit `source_lang` field. To still disable
// the identity-translation option, fall back to the patent_id prefix (which
// is the same ISO 3166-1 alpha-2 jurisdiction code, e.g. "DE-…"). Coverage is
// intentional — only the codes whose primary language maps to one of the 12
// SUPPORTED_TARGETS need to be listed.
const JURISDICTION_TO_LANG: Record<string, string> = {
  US: 'en', GB: 'en', AU: 'en', NZ: 'en', CA: 'en', IE: 'en', IN: 'en',
  DE: 'de', AT: 'de',
  FR: 'fr', BE: 'fr', LU: 'fr',
  ES: 'es',
  IT: 'it',
  PT: 'pt', BR: 'pt',
  JP: 'ja',
  KR: 'ko',
  CN: 'zh-Hans',
  TW: 'zh-Hant', HK: 'zh-Hant',
  RU: 'ru',
  SA: 'ar', AE: 'ar', EG: 'ar',
}

function inferSourceLang(explicit: string | null | undefined, patentId: string): string {
  if (explicit && explicit.trim()) return explicit.trim().toLowerCase()
  const prefix = (patentId || '').slice(0, 2).toUpperCase()
  return JURISDICTION_TO_LANG[prefix] || ''
}

export function PatentTranslate({
  patentId,
  sourceLang,
  sourceTitle,
  sourceAbstract,
  sourceClaims,
}: Props) {
  const sourceLangNorm = inferSourceLang(sourceLang, patentId)
  const [target, setTarget] = useState<string>(() => {
    return sourceLangNorm === 'en' ? 'fr' : 'en'
  })
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<PatentTranslationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  // QUICK-TRANSLATE-AA: toggle between AWS Translate (quick) and Bedrock Claude (accurate).
  const [useQuick, setUseQuick] = useState(false)
  const wrapRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (ev: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  const onTranslate = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await translatePatent(patentId, target, useQuick ? 'quick' : undefined)
      setResult(data)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown error'
      setError(`Translation failed: ${msg}`)
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  const currentLabel = LANGS.find((l) => l.code === target)?.label ?? target

  return (
    <div
      data-testid="patent-translate-panel"
      data-marker="PATENT-TRANSLATE-AA"
      className="mt-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-3">
        <Languages className="h-4 w-4 text-slate-600" aria-hidden />
        <h3 className="text-sm font-semibold text-slate-900">Translate</h3>
      </div>

      {/* QUICK-TRANSLATE-AA: mode toggle — AWS Translate vs Bedrock Claude */}
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          data-testid="patent-translate-toggle"
          aria-pressed={useQuick}
          onClick={() => { setUseQuick((q) => !q); setResult(null) }}
          className={
            'inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs font-medium transition-colors ' +
            (useQuick
              ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
              : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50')
          }
          title={useQuick ? 'Quick mode (AWS Translate) — faster, title+abstract only' : 'Accurate mode (AI) — full translation including claims'}
        >
          {useQuick ? 'Quick (AWS Translate)' : 'Accurate (AI)'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div ref={wrapRef} className="relative">
          <button
            type="button"
            id="patent-translate-select"
            data-testid="patent-translate-select"
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 hover:bg-slate-50 min-w-[16rem]"
          >
            <span className="flex-1 text-left truncate">{currentLabel}</span>
            <ChevronDown className="h-4 w-4 text-slate-500" aria-hidden />
          </button>
          {open && (
            <ul
              role="listbox"
              data-testid="patent-translate-options"
              className="absolute left-0 top-full z-20 mt-1 w-72 max-h-72 overflow-y-auto rounded border border-slate-200 bg-white py-1 shadow-lg"
            >
              {LANGS.map((l) => {
                const isSource = !!sourceLangNorm && l.code.toLowerCase() === sourceLangNorm
                const isSelected = l.code === target
                return (
                  <li key={l.code}>
                    <button
                      type="button"
                      role="option"
                      data-testid={`patent-translate-option-${l.code}`}
                      data-disabled-source={isSource ? 'true' : 'false'}
                      aria-selected={isSelected}
                      aria-disabled={isSource}
                      disabled={isSource}
                      onClick={() => {
                        if (isSource) return
                        setTarget(l.code)
                        setOpen(false)
                      }}
                      className={
                        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ' +
                        (isSource
                          ? 'text-slate-400 cursor-not-allowed bg-slate-50'
                          : 'text-slate-900 hover:bg-blue-50 hover:text-blue-900') +
                        (isSelected ? ' bg-blue-50 text-blue-900 font-medium' : '')
                      }
                    >
                      <span className="flex-1 truncate">{l.label}</span>
                      {isSelected && <Check className="h-4 w-4 text-blue-600" aria-hidden />}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
        <button
          type="button"
          data-testid="patent-translate-btn"
          onClick={onTranslate}
          disabled={loading}
          className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-slate-400"
        >
          {loading ? 'Translating…' : 'Translate'}
        </button>
        {result?.cached && (
          <span
            data-testid="patent-translate-cached-badge"
            className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700"
          >
            cached
          </span>
        )}
      </div>

      {error && (
        <p
          data-testid="patent-translate-error"
          className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700"
        >
          {error}
        </p>
      )}

      {result && !error && (
        <div
          data-testid="patent-translate-pane"
          dir={result.direction === 'rtl' ? 'rtl' : 'ltr'}
          className="mt-3 rounded border border-slate-200 bg-slate-50 p-3"
        >
          <h4
            data-testid="patent-translate-title"
            className="text-base font-semibold text-slate-900 mb-2"
          >
            {result.title}
          </h4>
          <p
            data-testid="patent-translate-abstract"
            className="text-sm text-slate-700 whitespace-pre-wrap mb-3"
          >
            {result.abstract}
          </p>
          {result.claims && result.claims.length > 0 && (
            <ol
              data-testid="patent-translate-claims"
              className="list-decimal pl-5 text-sm text-slate-700 space-y-1"
            >
              {result.claims.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ol>
          )}
          <p className="mt-3 text-xs text-slate-500">
            {result.source_lang_detected
              ? `Source language: ${result.source_lang_detected} → ${result.target_lang}`
              : `Target: ${result.target_lang}`}
          </p>
        </div>
      )}

      {!result && !error && (sourceTitle || sourceAbstract || sourceClaims) && (
        <p className="mt-3 text-xs text-slate-500">
          Click Translate to translate the title, abstract, and claims into the
          selected language. Patent identifiers, CPC codes, and inventor names
          are not translated.
        </p>
      )}
    </div>
  )
}
